import { useMutation, UseMutationResult, useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import { AttachmentTypesResponse, AttendanceReportListResponse, CustomFilterSettings, QueryOption, ReportAccessData, ReportAccessResponse, ReportResponse, ReportScheduleEmailType, SavedReportDataType, SavedReportsListResponse } from '../@types';
import { createReport, createSavedReportAccess, createScheduleReport, deleteReportById, getAttachmentTypes, getAttendanceReports, getDeafultReportByReportType, getReportAccessByReportCode, getReportByCode, getReportScheduleDataByReportId, getSavedReportAccessByReportId, getSavedReportsByCreatedId, sendSavedReportTestEmail, updateBulkReports, updateReportAccessDataById, updateReportByCode, updateReportScheduleEmailByReprtId } from '../api/report';
import { AttendanceReportsKey, defaultReportsKey, GetAttachmentTypesKey, GetReportsKey, SavedReportsAccessKey, SavedReportsKey, ScheduleReportKey } from '../configs/reports';

export const useGetAllAttendanceReports = (queryOption: QueryOption, customFilter?: CustomFilterSettings): UseQueryResult<AttendanceReportListResponse> => {
  return useQuery({
    queryKey: [AttendanceReportsKey, queryOption],
    queryFn: () => getAttendanceReports(queryOption, customFilter)
  });
};

export const useGetSavedReports = (queryOption: QueryOption): UseQueryResult<SavedReportsListResponse> => {
  return useQuery({
    queryKey: [GetReportsKey, queryOption.filterModel],
    queryFn: () => getSavedReportsByCreatedId(queryOption),
    enabled: false
  });
};

export const useAddNewReport = (): UseMutationResult<ReportResponse> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: any) => {
      return await createReport(data);
    },

    onSuccess: async (response: any) => {
      await queryClient.invalidateQueries({
        queryKey: [SavedReportsKey]
      });
      await queryClient.invalidateQueries({
        queryKey: [SavedReportsKey, response.id]
      });
    }
  });
};

export const useGetReportByCode = (code: string): UseQueryResult<SavedReportDataType> => {
  return useQuery({
    queryKey: [SavedReportsKey, code],
    queryFn: () => getReportByCode(code),
    enabled: false
  });
};

export const useUpdateReportByCode = (code: string): UseMutationResult<ReportResponse> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: any) => {
      return await updateReportByCode(code, data);
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [SavedReportsKey, code]
      });
      await queryClient.invalidateQueries({
        queryKey: [SavedReportsKey]
      });
    }
  }); 
};

export const useAddNewSavedReportAccess = (): UseMutationResult<ReportAccessData> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: any) => {
      return await createSavedReportAccess(data.reportId, data.data);
    },

    onSuccess: async (response: any) => {
      await queryClient.invalidateQueries({
        queryKey: [SavedReportsAccessKey]
      });
      await queryClient.invalidateQueries({
        queryKey: [SavedReportsAccessKey, response.id]
      });
    }
  });
};

export const useUpdateReportAccessDataById = (): UseMutationResult<ReportAccessData> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: any) => {
      return await updateReportAccessDataById(data.id, data.data);
    },

    onSuccess: async (data: any) => {
      await queryClient.invalidateQueries({
        queryKey: [SavedReportsAccessKey, data.id]
      });
      await queryClient.invalidateQueries({
        queryKey: [SavedReportsAccessKey]
      });
    }
  }); 
};

export const useGetShareAccessDataByReportId = (reportId: string): UseQueryResult<ReportAccessResponse> => {
  return useQuery({
    queryKey: [SavedReportsAccessKey, reportId],
    queryFn: () => getSavedReportAccessByReportId(reportId),
    enabled: false,
    gcTime: 0
  });
};

export const useGetReportAccessByReportCode = (code: string): UseQueryResult<ReportAccessData> => {
  return useQuery({
    queryKey: [SavedReportsAccessKey, code],
    queryFn: () => getReportAccessByReportCode(code),
    enabled: false,
    gcTime: 0
  });
};

export const useGetAttachmentTypes = (): UseQueryResult<AttachmentTypesResponse> => {
  return useQuery({
    queryKey: [GetAttachmentTypesKey],
    queryFn: () => getAttachmentTypes()
  });
};

export const useAddScheduleReport = (): UseMutationResult<ReportScheduleEmailType> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: any) => {
      return await createScheduleReport(data);
    },

    onSuccess: async (response: any) => {
      await queryClient.invalidateQueries({
        queryKey: [ScheduleReportKey]
      });
      await queryClient.invalidateQueries({
        queryKey: [ScheduleReportKey, response.id]
      });
    }
  });
};

export const useGetScheduleReportDataByReportId = (reportId: string): UseQueryResult<ReportScheduleEmailType> => {
  return useQuery({
    queryKey: [ScheduleReportKey, reportId],
    queryFn: () => getReportScheduleDataByReportId(reportId),
    enabled: false
  });
};

export const useUpdateReportScheduleEmailByReportId = (reportId: string): UseMutationResult<ReportScheduleEmailType> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: any) => {
      return await updateReportScheduleEmailByReprtId(reportId, data);
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [ScheduleReportKey, reportId]
      });
      await queryClient.invalidateQueries({
        queryKey: [ScheduleReportKey]
      });
    }
  }); 
};

export const useGetDefaultReportByReportType = (reportTypeName: string): UseQueryResult<SavedReportDataType[]> => {
  return useQuery({
    queryKey: [defaultReportsKey, reportTypeName],
    queryFn: () => getDeafultReportByReportType(reportTypeName),
    gcTime: 0,
    enabled: false
  });
};

export const useUpdateBulkReports = (): UseMutationResult => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: any) => {
      const response = await updateBulkReports(data);
      return response;
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [defaultReportsKey]
      });
    }
  });
};

export const useDeleteReportAccessDataById = (): UseMutationResult => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (reportId: any) => {
      return await deleteReportById(reportId);
    },

    onSuccess: async (response: any) => {
      await queryClient.invalidateQueries({
        queryKey: [GetReportsKey, response.id]
      });
    }
  });
};

export const useSendSavedReportTestEmail = (): UseMutationResult<{ success: boolean; message: string }> => {
  return useMutation({
    mutationFn: async (data: any) => {
      return await sendSavedReportTestEmail(data);
    }
  });
};